import { ArrowClockwise, ArrowCounterClockwise, FileText, SignIn as SignInIcon, SignOut } from '@phosphor-icons/react';
import { displayName, signOut, useAuth } from '../auth/session';
import { signInConfigured } from '../lib/supabase';
import w from './Workspace.module.css';
import { useStore } from '../state/store';
import { Button } from '../ui/kit';
import { shortCommit } from '../lib/api';
import { useBackendStatus } from '../useBackendStatus';

const WEB_COMMIT = shortCommit(import.meta.env.VITE_COMMIT ?? 'dev');
const ENV_LABEL = import.meta.env.VITE_ENV_LABEL ?? 'Local';

function Account() {
  const auth = useAuth();
  const setSigningIn = useStore((s) => s.setSigningIn);
  if (!signInConfigured || auth.state === 'loading') return null;
  if (auth.state === 'signed-in') {
    return (
      <span className={w.account}>
        <span className={w.who} title={auth.user.email ?? ''}>{displayName(auth.user)}</span>
        <Button variant="ghost" icon={<SignOut size={15} />} aria-label="Sign out" title="Sign out" onClick={() => void signOut()} />
      </span>
    );
  }
  return <Button icon={<SignInIcon size={15} />} onClick={() => setSigningIn(true)}>Sign in</Button>;
}

function Connection() {
  const status = useBackendStatus();
  const text = status.state === 'ok' ? `Server ${shortCommit(status.version.commit)}` : status.state === 'checking' ? 'Server…' : 'Server offline';
  return <span className={w.meta} data-state={status.state} title={`Web build ${WEB_COMMIT}${status.state === 'unreachable' ? ` · ${status.reason}` : ''}. Projects are stored in this browser.`}>{text}</span>;
}

export function TopBar() {
  const doc = useStore((s) => s.doc);
  const saveState = useStore((s) => s.saveState);
  const past = useStore((s) => s.past);
  const future = useStore((s) => s.future);
  const screen = useStore((s) => s.screen);
  const { undo, redo, closeProject, setScreen } = useStore.getState();
  return (
    <header className={w.topbar}>
      <button type="button" className={w.wordmark} onClick={closeProject} title="All projects">Extent</button>
      {doc && (
        <>
          <span className={w.crumbSep}>/</span>
          <span className={w.crumb}>{doc.meta.name}</span>
          <span className={w.draft}>Draft</span>
          <span className={w.save} data-state={saveState}>{{ saved: 'Saved in this browser', saving: 'Saving…', error: 'Not saved: browser storage refused' }[saveState]}</span>
        </>
      )}
      <span className={w.spacer} />
      {doc && (
        <span className={w.undo}>
          <Button variant="ghost" icon={<ArrowCounterClockwise size={15} />} disabled={!past.length} onClick={undo} title={past.length ? `Undo: ${past[past.length - 1]!.label} (Ctrl+Z)` : 'Nothing to undo'} aria-label="Undo" />
          <Button variant="ghost" icon={<ArrowClockwise size={15} />} disabled={!future.length} onClick={redo} title={future.length ? `Redo: ${future[0]!.label} (Ctrl+Shift+Z)` : 'Nothing to redo'} aria-label="Redo" />
        </span>
      )}
      {doc && (screen === 'workspace'
        ? <Button icon={<FileText size={15} />} onClick={() => setScreen('dossier')} title="Data sheets, calculation sheets and drawings">Dossier</Button>
        : <Button variant="primary" onClick={() => setScreen('workspace')}>Back to the model</Button>)}
      <Account />
      <Connection />
      <span className={w.env}>{ENV_LABEL}</span>
    </header>
  );
}
